<script setup lang="ts">
import DressCodeModel from './DressCodeModel.vue'

const props = defineProps<{
  roles: Record<string, any>
  brand_color: string
}>()
const activeTab = ref(Object.keys(props.roles)[0])
const formatGuidelines = (guidelines: any) => {
  return Object.entries(guidelines).map(([key, value]) => {
    if (typeof value === 'string') {
      return { title: key, content: value }
    }
    return {
      title: key,
      subItems: Object.entries(value as object).map(([subKey, subValue]) => ({
        title: subKey,
        content: subValue
      }))
    }
  })
}
const getButtonStyle = (isActive: boolean) => {
  if (isActive) {
    return {
      borderLeftColor: props.brand_color,
      color: props.brand_color,
      backgroundColor: `${props.brand_color}10`
    }
  }
  return {}
}
</script>

<template>
  <div
    class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
  >
    <!-- Change from fixed grid to responsive layout -->
    <div class="flex flex-col md:grid md:grid-cols-[200px_1fr] h-full">
      <!-- Mobile-friendly sidebar -->
      <div class="border-b md:border-b-0 md:border-r border-gray-200">
        <div class="flex md:flex-col overflow-x-auto md:overflow-x-visible">
          <button
            v-for="role in Object.keys(roles)"
            :key="role"
            :class="[
              'px-4 py-3 text-sm font-medium text-left transition-all duration-200 whitespace-nowrap',
              'md:border-l-4',
              activeTab === role
                ? 'border-b-2 md:border-b-0 md:border-l-4'
                : 'text-gray-500 hover:text-gray-700 border-transparent hover:bg-gray-50'
            ]"
            :style="getButtonStyle(activeTab === role)"
            @click="activeTab = role"
          >
            {{ role }}
          </button>
        </div>
      </div>

      <!-- Content area -->
      <div class="p-4 md:p-6">
        <div class="flex flex-col md:flex-row gap-6">
          <div class="flex-1">
            <div class="divide-y divide-gray-200">
              <div
                v-for="(item, index) in formatGuidelines(roles[activeTab])"
                :key="index"
                class="py-3 first:pt-0 last:pb-0"
              >
                <div
                  v-if="!item.subItems"
                  class="flex flex-col md:flex-row md:gap-2"
                >
                  <span class="font-medium text-gray-700 md:min-w-[100px]">{{ item.title }}:</span>
                  <span class="text-gray-600">{{ item.content }}</span>
                </div>
                <div v-else>
                  <h5 class="font-medium text-gray-700 mb-2">
                    {{ item.title }}
                  </h5>
                  <div class="ml-2 md:ml-4 space-y-2">
                    <div
                      v-for="(subItem, subIndex) in item.subItems"
                      :key="subIndex"
                      class="flex flex-col md:flex-row md:gap-2"
                    >
                      <span class="font-medium text-gray-600">{{ subItem.title }}:</span>
                      <span class="text-gray-600">{{ subItem.content }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Model is now shown at the bottom on mobile -->
          <div class="mt-4 md:mt-0 md:w-64 md:flex-shrink-0">
            <DressCodeModel
              :guidelines="roles[activeTab]"
              :brand_color="brand_color"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.grid {
  min-height: 400px;
}

/* Mobile role navigation styles */
@media (max-width: 768px) {
  /* Container for role buttons with scroll indication */
  .flex.md\:flex-col {
    position: relative;
    padding-right: 12px; /* Extra space to show more content is available */
    -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
    scrollbar-width: none; /* Hide scrollbar on Firefox */
    scroll-snap-type: x mandatory; /* Improve scroll experience */
  }

  .flex.md\:flex-col::-webkit-scrollbar {
    display: none; /* Hide scrollbar on Chrome/Safari */
  }

  /* Make each button an appropriate size without cutting text */
  .flex.md\:flex-col button {
    min-width: max-content; /* Allow buttons to be as wide as needed */
    padding-left: 16px;
    padding-right: 16px;
    scroll-snap-align: start; /* Improve scroll experience */
  }

  /* Add scroll indication with gradient */
  .flex.md\:flex-col::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    width: 20px;
    background: linear-gradient(to right, transparent, white);
    pointer-events: none;
  }
}

@media (min-width: 769px) {
  /* For desktop: wrap text and set fixed width */
  .flex.md\:flex-col button {
    white-space: normal;
    width: 100%;
    word-break: break-word;
    hyphens: auto;
  }
}
</style>
